import React, { useState, useEffect } from 'react';
import axios from 'axios'; // Import axios for API calls

const BookingCalendar = () => {
  const today = new Date();
  const currentDay = today.getDate(); // Numeric day of the month (1-31)
  const currentMonthIndex = today.getMonth(); // 0-indexed month (0 for Jan, 11 for Dec)
  const currentYear = today.getFullYear();

  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  const [selectedMonth, setSelectedMonth] = useState(currentMonthIndex);
  const [actualBookings, setActualBookings] = useState([]); // State to hold fetched and transformed bookings
  const [loading, setLoading] = useState(true); // Corrected: Initialized useState with true
  const [error, setError] = useState(null);

  // Calculate daysInMonth dynamically based on the selectedMonth and currentYear
  const daysInMonth = new Date(currentYear, selectedMonth + 1, 0).getDate();

  // Helper function to get the display status (CSS class) for a booking
  const getBookingDisplayStatus = (booking) => {
    // Directly map backend status to specific CSS class names
    switch (booking.status) {
      case 'Reserved':
      case 'Confirmed': // Assuming 'Confirmed' also falls under 'Reserved' for coloring
        return 'status-reserved';
      case 'Checked-in':
        return 'status-checked-in';
      case 'Checked-out':
        return 'status-checked-out';
      case 'Cancelled':
        return 'status-cancelled';
      case 'New': // If 'New' is a distinct backend status
        return 'status-new';
      default:
        return 'status-default'; // Fallback for any unhandled status
    }
  };


  // useEffect to fetch data when the component mounts or selectedMonth/currentYear changes
  useEffect(() => {
    const fetchAndProcessBookings = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch hotel bookings data
        const bookingsResponse = await axios.get('https://admin.thepridehotel.in/api/hotel_bookings');
        const hotelBookings = bookingsResponse.data;

        // Fetch room inventory data to get room types by ID (if booking only has room_id)
        const inventoryResponse = await axios.get('https://admin.thepridehotel.in/api/inventory');
        const roomTypeMap = new Map(inventoryResponse.data.map(room => [room.id, room.room_type]));

        const transformedData = hotelBookings
          // Removed the filter for 'Cancelled' bookings so they can be displayed
          .map(booking => {
            const checkInDate = new Date(booking.check_in);
            const checkOutDate = new Date(booking.check_out);

            // Ensure booking is within the current calendar year.
            if (checkInDate.getFullYear() !== currentYear && checkOutDate.getFullYear() !== currentYear) {
              return null;
            }

            // Determine the booking's effective start and end days within the *selected month* view
            const startOfMonth = new Date(currentYear, selectedMonth, 1);
            const endOfMonth = new Date(currentYear, selectedMonth, daysInMonth);

            // Calculate overlap period
            const overlapStartMs = Math.max(checkInDate.getTime(), startOfMonth.getTime());
            const overlapEndMs = Math.min(checkOutDate.getTime(), endOfMonth.getTime());

            // If no overlap or booking is zero duration within the month
            if (overlapStartMs >= overlapEndMs) {
                return null;
            }

            const effectiveStartDay = new Date(overlapStartMs).getDate();
            const effectiveEndDay = new Date(overlapEndMs).getDate();

            // Determine display name
            const displayName = booking.guest_name || roomTypeMap.get(booking.room_id) || booking.room_type || `Booking ${booking.id}`;

            return {
              id: booking.id, // Keep original ID for key prop
              name: displayName,
              startDay: effectiveStartDay,
              endDay: effectiveEndDay,
              displayStatus: getBookingDisplayStatus(booking), // Calculate display status
              // Store original month indices for filtering in `filteredBookings` if needed,
              // or for more complex date span rendering.
              originalCheckInMonth: checkInDate.getMonth(),
              originalCheckOutMonth: checkOutDate.getMonth(),
            };
          })
          .filter(Boolean); // Remove any null entries (filtered out bookings)

        setActualBookings(transformedData);
      } catch (err) {
        console.error('Error fetching calendar data:', err);
        setError('Failed to load calendar data. Please ensure your backend server is running and accessible at https://admin.thepridehotel.in.');
      } finally {
        setLoading(false);
      }
    };

    fetchAndProcessBookings();
  }, [selectedMonth, currentYear, daysInMonth]); // Re-fetch whenever selected month or year changes

  // Filter bookings to show only those relevant to the selected month.
  // The `transformedData` already clips bookings to the month boundaries,
  // so this filter ensures we only show bookings that actually appear in the selected month.
  const filteredBookings = actualBookings.filter(booking => {
      // Check if the booking's effective displayed period is within the selected month
      // This is true if its start day or end day (after clipping) falls within the selected month
      // or if it spans across the entire selected month.
      const startsInSelectedMonth = booking.originalCheckInMonth === selectedMonth;
      const endsInSelectedMonth = booking.originalCheckOutMonth === selectedMonth;
      const spansSelectedMonth = booking.originalCheckInMonth < selectedMonth && booking.originalCheckOutMonth > selectedMonth;
      return startsInSelectedMonth || endsInSelectedMonth || spansSelectedMonth;
  });


  if (loading) {
    return <div className="calendar-container">Loading booking calendar...</div>;
  }

  if (error) {
    return <div className="calendar-container" style={{ color: 'red' }}>Error: {error}</div>;
  }

  return (
    <div className="calendar-container">
      <style>{`
        /* Import font moved from here to public/index.html or global CSS if using React properly for global styles */
        /* @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap'); */

        .calendar-container {
          font-family: 'Open Sans', sans-serif;
          padding: 20px;
          background: white;
          min-height: 100vh;
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }

        .badges {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .badge {
          padding: 5px 10px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
        }

        /* Distinct colors for badges and booking bars based on new status categories */
        .status-reserved { background: #E0F0FF; color: #3B82F6; } /* Soft Blue */
        .status-checked-in { background: #D1FAE5; color: #10B981; } /* Vibrant Green */
        .status-checked-out { background: #F3F4F6; color: #6B7280; } /* Muted Grey */
        .status-cancelled { background: #FEE2E2; color: #EF4444; } /* Light Red */
        .status-new { background: #FFFBE0; color: #CA8A04; } /* Soft Yellow/Orange */

        .actions {
          display: flex;
          gap: 10px;
          margin-top: 10px; /* Adjust for responsiveness if needed */
        }

        .actions input {
          padding: 8px 12px;
          border: 1px solid #ccc;
          border-radius: 8px;
          font-size: 14px;
        }

        .actions button {
          background: #996633;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 8px 16px;
          cursor: pointer;
          font-size: 14px;
          transition: background-color 0.2s ease;
        }
        .actions button:hover {
          background-color: #80552b;
        }

        .months {
          display: flex;
          justify-content: center;
          margin: 20px 0 10px;
          gap: 15px;
          font-weight: 500;
          color: #444;
          flex-wrap: wrap;
        }

        .months span {
          padding: 4px 12px;
          border-radius: 20px;
          cursor: pointer;
          transition: background-color 0.2s ease, color 0.2s ease;
        }

        .months span:hover:not(.active-month) {
          background-color: #f0f0f0;
        }

        .months .active-month {
          border: 1px solid #3f80f9;
          color: #3f80f9;
          background-color: #e0f0ff; /* Light background for active month */
        }

        .day-header {
          display: grid;
          /* grid-template-columns is set by inline style below */
          margin-bottom: 5px;
          border-left: 1px solid #eee;
        }

        .day-cell {
          text-align: center;
          font-weight: 500;
          color: #999;
          padding: 10px 0;
          border-right: 1px solid #eee;
          background-color: #fcfcfc; /* Slight background for day cells */
        }

        .circle-day {
          border: 1px solid #3f80f9;
          border-radius: 50%;
          width: 30px;
          height: 30px;
          margin: 0 auto;
          color: #3f80f9;
          line-height: 30px; /* Vertically center text */
          background-color: #e0f0ff; /* Light background for today's circle */
          font-weight: 700;
        }

        .booking-section {
          position: relative;
          margin-top: 10px;
          /* Set a dynamic height based on the maximum number of simultaneous bookings,
             or use overflow-y for scrolling if many overlaps. */
          min-height: 400px; /* Ensures grid background has some height */
          overflow-y: auto; /* Allow scrolling if booking bars exceed height */
        }

        .booking-grid-bg {
          display: grid;
          /* grid-template-columns is set by inline style below */
          border-left: 1px solid #eee;
        }

        .booking-grid-bg > div {
          border-right: 1px solid #eee;
          min-height: 400px; /* Ensures grid cells have height for context */
        }

        .booking-bars {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          /* Height must match or exceed .booking-grid-bg > div height to cover grid */
          min-height: 400px;
          /* overflow-y: hidden; This is now controlled by .booking-section */
        }

        .booking-bar {
          position: absolute; /* Changed to absolute to allow exact positioning */
          height: 30px;
          line-height: 30px;
          padding-left: 10px;
          border-radius: 8px;
          color: #444;
          font-size: 14px;
          opacity: 0.9;
          margin-top: 10px; /* Spacing between booking bars */
          white-space: nowrap; /* Prevent text wrapping */
          overflow: hidden; /* Hide overflow text */
          text-overflow: ellipsis; /* Add ellipsis for overflow text */
          box-shadow: 0 1px 3px rgba(0,0,0,0.08); /* Subtle shadow */
          transition: opacity 0.2s ease, transform 0.2s ease;
          cursor: pointer; /* Indicate clickable */
        }
        .booking-bar:hover {
            opacity: 1;
            transform: translateY(-2px);
        }


        /* Media Queries for Responsiveness */
        @media (max-width: 768px) {
          .header {
            flex-direction: column;
            align-items: flex-start;
            gap: 15px;
          }
          .actions {
            width: 100%;
            justify-content: space-between;
          }
          .actions input {
            flex-grow: 1;
          }
          .months {
            justify-content: flex-start;
          }
          .day-header, .booking-grid-bg {
            overflow-x: auto; /* Allow horizontal scrolling on smaller screens */
          }
          .day-header > div, .booking-grid-bg > div {
            min-width: 40px; /* Ensure cells are not too narrow */
          }
          .booking-bar {
            /* May need to adjust top spacing or implement a more complex layout
               for very small screens with many overlapping bookings */
          }
        }

        @media (max-width: 480px) {
          .calendar-container {
            padding: 15px;
          }
          .actions {
            flex-direction: column;
            gap: 10px;
          }
          .actions button {
            width: 100%;
          }
        }
      `}</style>

      <div className="header">
        <div className="badges">
          <span className="badge status-reserved">Reserved</span>
          <span className="badge status-checked-in">Checked In</span>
          <span className="badge status-checked-out">Checked Out</span>
          <span className="badge status-cancelled">Cancelled</span>
          <span className="badge status-new">New</span>
        </div>
        <div className="actions">
          <input type="text" placeholder="Search by room number" />
          <button>Create booking</button>
        </div>
      </div>

      <div className="months">
        {monthNames.map((month, i) => (
          <span
            key={month}
            className={i === selectedMonth ? 'active-month' : ''}
            onClick={() => setSelectedMonth(i)}
          >
            {month}
          </span>
        ))}
      </div>

      {/* Day headers */}
      <div className="day-header" style={{ gridTemplateColumns: `repeat(${daysInMonth}, 1fr)` }}>
        {Array.from({ length: daysInMonth }, (_, i) => {
          const isCurrentDay = i + 1 === currentDay && selectedMonth === currentMonthIndex;
          return (
            <div className="day-cell" key={i}>
              {isCurrentDay ? <div className="circle-day">{i + 1}</div> : i + 1}
            </div>
          );
        })}
      </div>

      {/* Booking section with background grid + overlay */}
      <div className="booking-section">
        {/* Background grid lines */}
        <div className="booking-grid-bg" style={{ gridTemplateColumns: `repeat(${daysInMonth}, 1fr)` }}>
          {Array.from({ length: daysInMonth }, (_, i) => (
            <div key={i}></div>
          ))}
        </div>

        {/* Booking bars overlay */}
        <div className="booking-bars">
          {filteredBookings.map((booking, index) => {
            // Calculate actual width and left position based on effective start/end days
            // The `endDay` is inclusive, so duration includes the end day itself.
            const durationDays = booking.endDay - booking.startDay + 1;
            const leftOffset = (booking.startDay - 1) * (100 / daysInMonth);
            const widthPercentage = durationDays * (100 / daysInMonth);

            return (
              <div
                key={booking.id}
                className={`booking-bar ${booking.displayStatus || ''}`} // Apply the calculated display status
                style={{
                  left: `${leftOffset}%`,
                  width: `${widthPercentage}%`,
                  top: `${index * 40}px`, // Simple vertical stacking for now (adjust 40px as needed)
                }}
              >
                {booking.name}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BookingCalendar;